/**
 * GIWAHS Adaptive Journey Core Engine — Observability (AJIP-16.02.003 · WS10)
 * ---------------------------------------------------------------------
 * Lightweight, in-memory event emitter. Provides instrumentation hooks
 * for future monitoring. This package establishes hooks only. Analytics
 * dashboards and reporting are explicitly out of scope (see directive
 * EXPLICIT EXCLUSIONS).
 *
 * Emitted events (see JourneyEngine)
 *   JOURNEY_STARTED, JOURNEY_PAUSED, JOURNEY_RESUMED,
 *   JOURNEY_COMPLETED, JOURNEY_CANCELLED, JOURNEY_FAILED
 *
 * The emitter never performs I/O, never touches storage, and never
 * makes a network call.
 */

const OBSERVABILITY_EVENTS = Object.freeze({
  JOURNEY_STARTED: 'JOURNEY_STARTED',
  JOURNEY_PAUSED: 'JOURNEY_PAUSED',
  JOURNEY_RESUMED: 'JOURNEY_RESUMED',
  JOURNEY_COMPLETED: 'JOURNEY_COMPLETED',
  JOURNEY_CANCELLED: 'JOURNEY_CANCELLED',
  JOURNEY_FAILED: 'JOURNEY_FAILED',
});

const ALL_OBSERVABILITY_EVENTS = Object.freeze(Object.values(OBSERVABILITY_EVENTS));

class Observability {
  constructor() {
    this._handlers = new Map();
    for (const k of ALL_OBSERVABILITY_EVENTS) this._handlers.set(k, new Set());
    this._enabled = true;
  }

  on(eventKind, handler) {
    if (!ALL_OBSERVABILITY_EVENTS.includes(eventKind)) return () => {};
    if (typeof handler !== 'function') return () => {};
    this._handlers.get(eventKind).add(handler);
    return () => this._handlers.get(eventKind).delete(handler);
  }

  off(eventKind, handler) {
    if (!ALL_OBSERVABILITY_EVENTS.includes(eventKind)) return false;
    return this._handlers.get(eventKind).delete(handler);
  }

  emit(eventKind, payload) {
    if (!this._enabled) return 0;
    if (!ALL_OBSERVABILITY_EVENTS.includes(eventKind)) return 0;
    const handlers = this._handlers.get(eventKind);
    let count = 0;
    for (const h of handlers) {
      try {
        h(Object.freeze({ event: eventKind, at: Date.now(), payload }));
        count += 1;
      } catch (_e) {
        // Handler errors must never affect the engine. Swallow and continue.
      }
    }
    return count;
  }

  disable() { this._enabled = false; }
  enable() { this._enabled = true; }
  isEnabled() { return this._enabled; }
  listenerCount(eventKind) {
    if (!ALL_OBSERVABILITY_EVENTS.includes(eventKind)) return 0;
    return this._handlers.get(eventKind).size;
  }
  clear() {
    for (const set of this._handlers.values()) set.clear();
  }
}

export { OBSERVABILITY_EVENTS, ALL_OBSERVABILITY_EVENTS, Observability };
